import React, { useState, useEffect } from 'react';
import { ThreeDots } from 'react-loader-spinner';
import { apiCall } from '../../utils/apiUtils';

const AdminCreditsTab = () => {
    const [targetEmail, setTargetEmail] = useState('');
    const [delta, setDelta] = useState('');
    const [reason, setReason] = useState('');
    const [adjustResult, setAdjustResult] = useState('');
    const [adjustError, setAdjustError] = useState('');
    const [adjusting, setAdjusting] = useState(false);

    const [stuckReservations, setStuckReservations] = useState([]);
    const [loadingStuck, setLoadingStuck] = useState(true);
    const [stuckError, setStuckError] = useState('');

    useEffect(() => {
        loadStuckReservations();
    }, []);

    const loadStuckReservations = async () => {
        setLoadingStuck(true);
        setStuckError('');
        try {
            const response = await apiCall('/billing/admin/stuck-reservations', null, { method: 'GET' });
            setStuckReservations(response.stuckReservations || []);
        } catch (err) {
            console.error('Failed to load stuck reservations:', err);
            setStuckError('Failed to load stuck reservations.');
        } finally {
            setLoadingStuck(false);
        }
    };

    const handleAdjust = async (e) => {
        e.preventDefault();
        if (!targetEmail || !delta) return;
        setAdjusting(true);
        setAdjustResult('');
        setAdjustError('');
        try {
            const response = await apiCall('/billing/admin/adjust-credits', {
                targetEmail,
                delta: parseInt(delta, 10),
                reason: reason || 'admin_adjustment',
            }, { method: 'POST' });
            setAdjustResult(`Done. New balance: ${response.newBalance} credits.`);
            setDelta('');
            setReason('');
        } catch (err) {
            console.error('Failed to adjust credits:', err);
            setAdjustError('Failed to adjust credits. Check the email and delta.');
        } finally {
            setAdjusting(false);
        }
    };

    const formatDate = (iso) =>
        new Date(iso).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });

    return (
        <div>
            <h2 className="admin-section-title">Credit Management</h2>

            <div className="admin-credits-form">
                <h3 className="admin-form-title">Adjust User Credits</h3>
                <form onSubmit={handleAdjust}>
                    <input
                        type="email"
                        placeholder="User email"
                        value={targetEmail}
                        onChange={(e) => setTargetEmail(e.target.value)}
                        required
                        className="admin-form-input admin-form-input--full"
                    />
                    <div className="form-row">
                        <input
                            type="number"
                            placeholder="Delta (e.g. 30 or -10)"
                            value={delta}
                            onChange={(e) => setDelta(e.target.value)}
                            required
                            className="admin-form-input"
                        />
                        <input
                            type="text"
                            placeholder="Reason (optional)"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            className="admin-form-input admin-form-input--grow"
                        />
                        <button type="submit" className="submit-btn" disabled={adjusting || !targetEmail || !delta}>
                            {adjusting ? 'Adjusting…' : 'Apply'}
                        </button>
                    </div>
                </form>
                {adjustResult && <div className="admin-credits-result">{adjustResult}</div>}
                {adjustError && <div className="admin-credits-error">{adjustError}</div>}
            </div>

            <h3 className="admin-subheading-row">
                Stuck Reservations (&gt;15 min)
                <button type="button" onClick={loadStuckReservations} className="admin-btn-ghost">
                    Refresh
                </button>
            </h3>

            {stuckError && <div className="admin-empty">{stuckError}</div>}

            {loadingStuck ? (
                <div className="loader-container">
                    <ThreeDots color="#00BFFF" height={40} width={40} />
                </div>
            ) : (
                <div className="admin-table-wrapper">
                    {stuckReservations.length > 0 ? (
                        <table>
                            <thead>
                                <tr>
                                    <th>User</th>
                                    <th>Credits Reserved</th>
                                    <th>Action Type</th>
                                    <th>Created At</th>
                                </tr>
                            </thead>
                            <tbody>
                                {stuckReservations.map((r) => (
                                    <tr key={r.id}>
                                        <td>{r.userId}</td>
                                        <td>{Math.abs(r.creditsDelta ?? 0)}</td>
                                        <td>{r.actionType || '—'}</td>
                                        <td>{formatDate(r.createdAt)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div className="admin-empty">No stuck reservations found.</div>
                    )}
                </div>
            )}
        </div>
    );
};

export default AdminCreditsTab;
